/**
 * InterviewQuestionsPreviewModal Component
 * Shows generated interview questions before starting the interview.
 * For Express/Standard interviews:
 * - Groups questions by skill
 * - Allows selecting/unselecting questions
 * - Allows adding new questions via AddQuestionsModal
 */

import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  XMarkIcon,
  PlayIcon,
  MicrophoneIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { aiInterviewsApi } from '../../services/api'
import AddQuestionsModal from './AddQuestionsModal'
import type { AIInterview, AIInterviewQuestion, AIInterviewType, AdditionalQuestion } from '../../types/interview'

interface InterviewQuestionsPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  interview: AIInterview
  questions: AIInterviewQuestion[]
  isGeneratingQuestions: boolean
  candidateName: string
  jobTitle: string
  origin?: 'job' | 'resume'
}

// Extended question type with skill property and selection state
interface SelectableQuestion extends AIInterviewQuestion {
  skill?: string
  skill_aspect_tested?: string
  category?: string
  is_additional?: boolean
  isSelected: boolean
  originalIndex: number
}

// Group questions by skill for Express/Standard interviews
function groupQuestionsBySkill(questions: SelectableQuestion[]): Map<string, SelectableQuestion[]> {
  const groups = new Map<string, SelectableQuestion[]>()

  for (const question of questions) {
    const skill = question.skill || 'General'
    if (!groups.has(skill)) {
      groups.set(skill, [])
    }
    groups.get(skill)!.push(question)
  }

  return groups
}

// Type config for display
const TYPE_LABELS: Record<AIInterviewType, { label: string; color: string }> = {
  ai_interview: { label: 'AI Interview', color: 'green' },
  express: { label: 'Express Interview', color: 'orange' },
  standard: { label: 'Standard Interview', color: 'purple' },
}

export default function InterviewQuestionsPreviewModal({
  isOpen,
  onClose,
  interview,
  questions: initialQuestions,
  isGeneratingQuestions,
  candidateName,
  jobTitle,
  origin = 'resume',
}: InterviewQuestionsPreviewModalProps) {
  const navigate = useNavigate()
  const [showAddQuestionsModal, setShowAddQuestionsModal] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [isGeneratingAdditional, setIsGeneratingAdditional] = useState(false)

  // Initialize questions with selection state
  const [selectableQuestions, setSelectableQuestions] = useState<SelectableQuestion[]>([])

  // Reset questions when modal opens or initial questions change
  useEffect(() => {
    if (isOpen && initialQuestions.length > 0) {
      setSelectableQuestions(
        initialQuestions.map((q, index) => ({
          ...q,
          skill: (q as any).skill,
          skill_aspect_tested: (q as any).skill_aspect_tested,
          category: (q as any).category,
          is_additional: (q as any).is_additional,
          isSelected: true,
          originalIndex: index,
        }))
      )
      setHasChanges(false)
    }
  }, [isOpen, initialQuestions])

  // Determine if we should group questions by skill and allow editing
  const isSkillBased = interview.interview_type === 'express' || interview.interview_type === 'standard'
  const canEditQuestions = isSkillBased && interview.status === 'questions_generated'

  // Group questions by skill for Express/Standard interviews
  const groupedQuestions = useMemo(() => {
    if (!isSkillBased) return null
    return groupQuestionsBySkill(selectableQuestions)
  }, [selectableQuestions, isSkillBased])

  // Count selected questions
  const selectedCount = useMemo(() => {
    return selectableQuestions.filter(q => q.isSelected).length
  }, [selectableQuestions])

  // Count original vs added questions
  const { originalCount, addedCount } = useMemo(() => {
    const original = selectableQuestions.filter(q => !q.is_additional).length
    const added = selectableQuestions.filter(q => q.is_additional).length
    return { originalCount: original, addedCount: added }
  }, [selectableQuestions])

  if (!isOpen) return null

  const handleToggleQuestion = (index: number) => {
    if (!canEditQuestions) return

    setSelectableQuestions(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], isSelected: !updated[index].isSelected }
      return updated
    })
    setHasChanges(true)
  }

  const handleToggleAllInSkill = (skillName: string, select: boolean) => {
    if (!canEditQuestions) return

    setSelectableQuestions(prev =>
      prev.map(q => (q.skill === skillName ? { ...q, isSelected: select } : q))
    )
    setHasChanges(true)
  }

  const handleAddQuestions = (newQuestions: AdditionalQuestion[]) => {
    const startIndex = selectableQuestions.length
    const newSelectableQuestions: SelectableQuestion[] = newQuestions.map((q, i) => ({
      question_number: startIndex + i + 1,
      question: q.question,
      expected_answer: q.expected_answer,
      skill: q.skill,
      skill_aspect_tested: q.skill_aspect_tested,
      category: q.category,
      is_additional: true,
      isSelected: true,
      originalIndex: startIndex + i,
    }))

    setSelectableQuestions(prev => [...prev, ...newSelectableQuestions])
    setHasChanges(true)
  }

  const handleSaveChanges = async () => {
    if (!hasChanges) return

    setIsSaving(true)
    try {
      // Separate new questions from existing ones
      const newQuestions = selectableQuestions.filter(
        q => q.is_additional && q.originalIndex >= initialQuestions.length
      )

      // Step 1: Add new questions first if any
      if (newQuestions.length > 0) {
        await aiInterviewsApi.addQuestionsToInterview(
          interview.id,
          newQuestions.map(q => ({
            question: q.question,
            skill: q.skill || '',
            skill_aspect_tested: q.skill_aspect_tested,
            category: q.category,
            expected_answer: q.expected_answer,
            is_additional: true,
          }))
        )
      }

      // Step 2: Check if any questions were unselected
      const hasUnselected = selectableQuestions.some(q => !q.isSelected)

      if (hasUnselected) {
        // We need to calculate indices based on the NEW total after adding questions
        // Original questions: indices 0 to (initialQuestions.length - 1)
        // New questions: indices initialQuestions.length to (initialQuestions.length + newQuestions.length - 1)

        const selectedIndices: number[] = []

        // Check original questions
        selectableQuestions.forEach((q, localIndex) => {
          if (q.isSelected) {
            if (q.originalIndex < initialQuestions.length) {
              // This is an original question - use its original index
              selectedIndices.push(q.originalIndex)
            } else {
              // This is a new question - calculate its index after being added
              // New questions are added at the end, so their index is:
              // initialQuestions.length + (position among new questions)
              const newQuestionIndex = newQuestions.findIndex(
                nq => nq.originalIndex === q.originalIndex
              )
              if (newQuestionIndex !== -1) {
                selectedIndices.push(initialQuestions.length + newQuestionIndex)
              }
            }
          }
        })

        // Sort indices to maintain order
        selectedIndices.sort((a, b) => a - b)

        await aiInterviewsApi.updateSelectedQuestions(interview.id, selectedIndices)
      }

      toast.success('Questions updated successfully')
      setHasChanges(false)
    } catch (error: any) {
      console.error('Failed to save changes:', error)
      const message = error?.response?.data?.detail || 'Failed to save changes'
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleStartInterview = async () => {
    // Save changes if there are any before starting
    if (hasChanges) {
      await handleSaveChanges()
    }

    // Navigate to the interview recording page with origin param
    navigate(`/interviews/${interview.id}/record?origin=${origin}`)
    onClose()
  }

  const estimatedDuration = selectedCount * 3 // 3 minutes per question average
  const typeConfig = TYPE_LABELS[interview.interview_type as AIInterviewType] || TYPE_LABELS.ai_interview

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-10 mx-auto p-0 border w-[95%] max-w-3xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${
                typeConfig.color === 'green' ? 'bg-green-100' :
                typeConfig.color === 'orange' ? 'bg-orange-100' :
                'bg-purple-100'
              }`}>
                <MicrophoneIcon className={`w-6 h-6 ${
                  typeConfig.color === 'green' ? 'text-green-600' :
                  typeConfig.color === 'orange' ? 'text-orange-600' :
                  'text-purple-600'
                }`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">{typeConfig.label}</h2>
                  {isSkillBased && interview.selected_skills && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      typeConfig.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {interview.selected_skills.length} skill{interview.selected_skills.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">{candidateName} - {jobTitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {isGeneratingQuestions ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
                <p className="text-gray-600 font-medium">Generating interview questions...</p>
                <p className="text-sm text-gray-500 mt-2">
                  Our AI is creating tailored questions based on the job requirements.
                </p>
              </div>
            ) : selectableQuestions.length === 0 ? (
              <div className="text-center py-12">
                <QuestionMarkCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No Questions Generated</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Unable to generate questions. Please try again or contact support.
                </p>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="flex items-center space-x-3">
                      <QuestionMarkCircleIcon className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Total Questions</p>
                        <p className="font-semibold text-gray-900">{selectableQuestions.length}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="text-sm text-gray-500">Selected</p>
                        <p className="font-semibold text-gray-900">{selectedCount}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <ClockIcon className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Est. Duration</p>
                        <p className="font-semibold text-gray-900">~{estimatedDuration} min</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <PlusIcon className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="text-sm text-gray-500">Added</p>
                        <p className="font-semibold text-blue-600">{addedCount}</p>
                      </div>
                    </div>
                  </div>

                  {/* Legend */}
                  {isSkillBased && (
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-500 mb-2">Legend:</p>
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center space-x-2">
                          <span className={`w-4 h-4 rounded-full ${
                            typeConfig.color === 'orange' ? 'bg-orange-100 border border-orange-300' : 'bg-purple-100 border border-purple-300'
                          }`}></span>
                          <span className="text-xs text-gray-600">Original Questions ({originalCount})</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="w-4 h-4 rounded-full bg-blue-200 border border-blue-400"></span>
                          <span className="text-xs text-gray-600">Added Questions ({addedCount})</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="w-4 h-1 bg-blue-400 rounded"></span>
                          <span className="text-xs text-gray-600">Blue left border = Added</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Add Questions Button (for Express/Standard only) */}
                {canEditQuestions && (
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={() => setShowAddQuestionsModal(true)}
                      className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <PlusIcon className="w-4 h-4 mr-1" />
                      Add Questions
                    </button>
                  </div>
                )}

                {/* Questions List */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
                    Interview Questions Preview
                    {canEditQuestions && (
                      <span className="text-xs font-normal text-gray-500 ml-2">
                        (Click checkbox to select/unselect questions)
                      </span>
                    )}
                  </h3>

                  {/* Skill-grouped questions for Express/Standard interviews */}
                  {isSkillBased && groupedQuestions ? (
                    <div className="space-y-6">
                      {Array.from(groupedQuestions.entries()).map(([skillName, skillQuestions]) => {
                        const selectedInSkill = skillQuestions.filter(q => q.isSelected).length
                        const allSelectedInSkill = selectedInSkill === skillQuestions.length

                        return (
                          <div key={skillName} className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* Skill Header */}
                            <div className={`px-4 py-3 ${
                              typeConfig.color === 'orange' ? 'bg-orange-50 border-b border-orange-200' :
                              'bg-purple-50 border-b border-purple-200'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  {canEditQuestions && (
                                    <input
                                      type="checkbox"
                                      checked={allSelectedInSkill}
                                      onChange={() => handleToggleAllInSkill(skillName, !allSelectedInSkill)}
                                      className={`mr-3 h-4 w-4 rounded ${
                                        typeConfig.color === 'orange'
                                          ? 'text-orange-600 focus:ring-orange-500'
                                          : 'text-purple-600 focus:ring-purple-500'
                                      } border-gray-300`}
                                    />
                                  )}
                                  <h4 className={`font-medium ${
                                    typeConfig.color === 'orange' ? 'text-orange-800' : 'text-purple-800'
                                  }`}>
                                    {skillName}
                                  </h4>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  typeConfig.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                                  'bg-purple-100 text-purple-700'
                                }`}>
                                  {selectedInSkill}/{skillQuestions.length} selected
                                </span>
                              </div>
                            </div>

                            {/* Skill Questions */}
                            <div className="divide-y divide-gray-100">
                              {skillQuestions.map((question, index) => (
                                <div
                                  key={question.originalIndex}
                                  className={`p-4 transition-colors ${
                                    question.is_additional
                                      ? question.isSelected
                                        ? 'bg-blue-50 border-l-4 border-l-blue-400 hover:bg-blue-100'
                                        : 'bg-gray-100 border-l-4 border-l-gray-300 opacity-60'
                                      : question.isSelected
                                        ? 'hover:bg-gray-50'
                                        : 'bg-gray-100 opacity-60'
                                  }`}
                                >
                                  <div className="flex items-start space-x-3">
                                    {canEditQuestions && (
                                      <input
                                        type="checkbox"
                                        checked={question.isSelected}
                                        onChange={() => handleToggleQuestion(
                                          selectableQuestions.findIndex(q => q.originalIndex === question.originalIndex)
                                        )}
                                        className={`mt-1 h-4 w-4 rounded ${
                                          typeConfig.color === 'orange'
                                            ? 'text-orange-600 focus:ring-orange-500'
                                            : 'text-purple-600 focus:ring-purple-500'
                                        } border-gray-300`}
                                      />
                                    )}
                                    <span className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium ${
                                      question.is_additional
                                        ? 'bg-blue-200 text-blue-800'
                                        : typeConfig.color === 'orange'
                                          ? 'bg-orange-100 text-orange-700'
                                          : 'bg-purple-100 text-purple-700'
                                    }`}>
                                      {index + 1}
                                    </span>
                                    <div className="flex-1">
                                      <div className="flex items-start justify-between">
                                        <p className={`font-medium ${question.isSelected ? 'text-gray-900' : 'text-gray-500 line-through'}`}>
                                          {question.question}
                                        </p>
                                        {question.is_additional && (
                                          <span className="ml-2 flex-shrink-0 text-xs px-2 py-1 rounded-full bg-blue-500 text-white font-medium shadow-sm">
                                            + Added
                                          </span>
                                        )}
                                      </div>
                                      {question.skill_aspect_tested && (
                                        <p className="text-xs text-gray-500 mt-1">
                                          Aspect: {question.skill_aspect_tested}
                                        </p>
                                      )}
                                      {question.category && (
                                        <p className="text-xs text-blue-600 mt-1">
                                          Category: {question.category}
                                        </p>
                                      )}
                                      {question.expected_answer?.key_points && question.expected_answer.key_points.length > 0 && question.isSelected && (
                                        <div className={`mt-3 rounded-lg p-3 ${question.is_additional ? 'bg-blue-100' : 'bg-gray-50'}`}>
                                          <p className="text-xs font-medium text-gray-600 mb-2 flex items-center">
                                            <CheckCircleIcon className="w-4 h-4 mr-1 text-green-500" />
                                            Expected Key Points:
                                          </p>
                                          <ul className="text-sm text-gray-600 space-y-1">
                                            {question.expected_answer.key_points.map((point, i) => (
                                              <li key={i} className="flex items-start">
                                                <span className="text-green-500 mr-2">•</span>
                                                <span>{point}</span>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    /* Flat list for AI Interview */
                    selectableQuestions.map((question, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                      >
                        <div className="flex items-start space-x-3">
                          <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center bg-green-100 rounded-full text-sm font-medium text-green-700">
                            {question.question_number || index + 1}
                          </span>
                          <div className="flex-1">
                            <p className="text-gray-900 font-medium">{question.question}</p>
                            {question.expected_answer?.key_points && question.expected_answer.key_points.length > 0 && (
                              <div className="mt-3 bg-gray-50 rounded-lg p-3">
                                <p className="text-xs font-medium text-gray-600 mb-2 flex items-center">
                                  <CheckCircleIcon className="w-4 h-4 mr-1 text-green-500" />
                                  Expected Key Points:
                                </p>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {question.expected_answer.key_points.map((point, i) => (
                                    <li key={i} className="flex items-start">
                                      <span className="text-green-500 mr-2">•</span>
                                      <span>{point}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-between items-center">
            <p className="text-sm text-gray-500">
              Session ID: {interview.session_id}
              {hasChanges && <span className="ml-2 text-orange-600">(unsaved changes)</span>}
            </p>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              {hasChanges && canEditQuestions && (
                <button
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
              <button
                onClick={handleStartInterview}
                disabled={isGeneratingQuestions || isGeneratingAdditional || selectedCount === 0 || isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <PlayIcon className="w-4 h-4" />
                <span>{isGeneratingAdditional ? 'Generating...' : `Start Interview (${selectedCount} questions)`}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Questions Modal */}
      {showAddQuestionsModal && (
        <AddQuestionsModal
          isOpen={showAddQuestionsModal}
          onClose={() => setShowAddQuestionsModal(false)}
          interviewId={interview.id}
          interviewType={interview.interview_type}
          selectedSkills={(interview.selected_skills || []).map(s =>
            typeof s === 'string' ? s : s.name
          )}
          onQuestionsGenerated={handleAddQuestions}
          onGeneratingChange={setIsGeneratingAdditional}
        />
      )}
    </>
  )
}
